import React, { useEffect } from 'react';
import i18n from 'i18next';

import { Language } from '../shared/constants';
import {
  LocalStorageKey,
  LocalStorageService,
} from '../services/local-storage';
import {
  useLazyGetMyDetails,
  useUpdateUser,
} from '../services/redux/api/usersApi';
import { useLazyGetActiveLanguages } from '../services/redux/api/languagesAPI';
import { LoginUserStatus, useAuth } from './AuthContext';

const LanguageContext = React.createContext();
const defaultLanguageKey = Language.en;

const resolveLanguage = (languages, languageId) => {
  if (!languages?.length) {
    return null;
  }

  return (
    languages.find((language) => language.id === languageId) ||
    languages.find((language) => language.key === defaultLanguageKey) ||
    languages[0]
  );
};

function LanguageProvider(props) {
  const [getLanguagesApi, { data: languages, isFetching: isLoading }] =
    useLazyGetActiveLanguages();
  const [getMyDetails, { data: user }] = useLazyGetMyDetails();
  const currentLanguage = resolveLanguage(languages, user?.languageId);
  const [updateUser] = useUpdateUser();
  const { loginUserStatus } = useAuth();

  useEffect(() => {
    if (loginUserStatus === LoginUserStatus.loggedIn) {
      getMyDetails();
    }
  }, [loginUserStatus]);

  useEffect(() => {
    if (user && !languages) getLanguagesApi();

    if (languages && user) {
      const language = resolveLanguage(languages, user?.languageId);
      if (!language?.key) {
        return;
      }

      LocalStorageService.setValue(LocalStorageKey.language, language.key);
      i18n.changeLanguage(language.key);
    }
  }, [languages, user]);

  function setLanguage(languageId) {
    const language = resolveLanguage(languages, languageId);

    if (!language?.id) {
      return Promise.resolve();
    }

    return updateUser({ id: user.id, body: { languageId: language.id } })
      .unwrap()
      .then(() => {
        LocalStorageService.setValue(LocalStorageKey.language, language.key);
        i18n.changeLanguage(language.key);
      })
      .catch((e) => {
        console.log(e);
      });
  }

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        setLanguage,
      }}>
      {props.children}
    </LanguageContext.Provider>
  );
}

function useLanguage() {
  const context = React.useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within an LanguageProvider');
  }

  return context;
}

export { useLanguage, LanguageProvider };
